import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (!context) {
        throw new Error('useAdminAuth must be used within an AdminAuthProvider');
    }
    return context;
};

export const AdminAuthProvider = ({ children }) => {
    const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    const checkAdminAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/admin/verify`, {
                withCredentials: true
            });
            setIsAdminAuthenticated(response.data.authenticated);
            return true;
        } catch (error) {
            setIsAdminAuthenticated(false);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const adminLogin = async (username, password) => {
        try {
            const response = await axios.post(
                `${API}/admin/login`,
                { username, password },
                { withCredentials: true }
            );
            setIsAdminAuthenticated(true);
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.detail || 'Error de autenticación';
            return { success: false, error: message };
        }
    };

    const adminLogout = async () => {
        try {
            await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Admin logout error:', error);
        } finally {
            setIsAdminAuthenticated(false);
        }
    };

    useEffect(() => {
        checkAdminAuth();
    }, [checkAdminAuth]);

    const value = {
        isAdminAuthenticated,
        loading,
        adminLogin,
        adminLogout,
        checkAdminAuth
    };

    return (
        <AdminAuthContext.Provider value={value}>
            {children}
        </AdminAuthContext.Provider>
    );
};

export default AdminAuthContext;
